import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { BACKEND_URL } from '../../Constants';

// Import for react-draft-wysiwyg
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

function CalendarView() {
  const [courses, setCourses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Form states
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContentEditorState, setAnnouncementContentEditorState] = useState(
    () => EditorState.createEmpty()
  );

  // Get user role from local storage and determine permissions
  const userRole = localStorage.getItem('role');
  const canCreateAnnouncement = userRole === 'admin' || userRole === 'teacher';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch courses
        const coursesResponse = await axios.get(`${BACKEND_URL}/api/courses`);
        setCourses(coursesResponse.data);
        
        // Try to fetch announcements, but don't fail if endpoint doesn't exist
        try {
          const announcementsResponse = await axios.get(`${BACKEND_URL}/api/announcements`);
          setAnnouncements(announcementsResponse.data);
        } catch (announcementErr) {
          console.log('Announcements endpoint not available yet, using empty array');
          setAnnouncements([]);
        }
      } catch (err) {
        console.error('Error fetching courses for calendar:', err);
        setError('Failed to load courses for calendar.');
        toast.error('Failed to load courses for calendar.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDateClick = (info) => {
    if (!canCreateAnnouncement) {
      toast.warn('You do not have permission to create announcements.');
      return;
    }
    setSelectedDate(info.dateStr);
    setShowAnnouncementModal(true);
  };

  const handleEventClick = (arg) => {
    const eventType = arg.event.extendedProps.type;
    
    if (eventType === 'course') {
      const courseId = arg.event.id;
      const url = `/course/${courseId}`;
      window.open(url, '_blank');
    } else if (eventType === 'announcement') {
      setSelectedEvent(arg.event);
      setShowViewModal(true);
    }
  };

  const handleCreateAnnouncement = async () => {
    // Convert editor state to HTML
    const contentState = announcementContentEditorState.getCurrentContent();
    const contentHtml = draftToHtml(convertToRaw(contentState));
    
    if (!announcementTitle.trim() || !contentState.hasText()) {
      toast.error('Please fill in both title and content.');
      return;
    }

    try {
      const newAnnouncement = {
        title: announcementTitle,
        content: contentHtml,
        date: selectedDate,
        createdAt: new Date().toISOString()
      };

      // Try to save to backend, but fallback to local state if endpoint doesn't exist
      try {
        const response = await axios.post(`${BACKEND_URL}/api/announcements`, newAnnouncement);
        setAnnouncements(prev => [...prev, response.data]);
      } catch (backendErr) {
        console.log('Backend announcement endpoint not available, storing locally');
        // Fallback: store in local state with generated ID
        const localAnnouncement = {
          ...newAnnouncement,
          _id: Date.now().toString() // Simple ID generation
        };
        setAnnouncements(prev => [...prev, localAnnouncement]);
      }
      
      // Reset form and close modal
      setAnnouncementTitle('');
      setAnnouncementContentEditorState(EditorState.createEmpty());
      setShowAnnouncementModal(false);
      setSelectedDate(null);
      
      toast.success('Announcement created successfully!');
    } catch (err) {
      console.error('Error creating announcement:', err);
      toast.error('Failed to create announcement.');
    }
  };

  const handleDeleteAnnouncement = async () => {
    if (!selectedEvent || !canCreateAnnouncement) {
      toast.warn('You do not have permission to delete announcements.');
      return;
    }

    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      const announcementId = selectedEvent.id.replace('announcement-', '');
      
      // Try to delete from backend, but fallback to local state if endpoint doesn't exist
      try {
        await axios.delete(`${BACKEND_URL}/api/announcements/${announcementId}`);
      } catch (backendErr) {
        console.log('Backend delete endpoint not available, removing locally');
      }
      
      // Remove from local state regardless of backend response
      setAnnouncements(prev => prev.filter(announcement => announcement._id !== announcementId));
      
      // Close modal and reset
      setShowViewModal(false);
      setSelectedEvent(null);
      
      toast.success('Announcement deleted successfully!');
    } catch (err) {
      console.error('Error deleting announcement:', err);
      toast.error('Failed to delete announcement.');
    }
  };

  const handleCloseModal = () => {
    setShowAnnouncementModal(false);
    setShowViewModal(false);
    setAnnouncementTitle('');
    setAnnouncementContentEditorState(EditorState.createEmpty());
    setSelectedDate(null);
    setSelectedEvent(null);
  };

  const getPlainTextFromDraft = (draftJson) => {
    try {
      const parsed = typeof draftJson === 'string' ? JSON.parse(draftJson) : draftJson;
      return parsed.blocks.map(block => block.text).join('\n');
    } catch (err) {
      return '';
    }
  };

  // Map courses to calendar events
  const courseEvents = courses.map(course => ({
    id: course._id,
    title: course.title,
    start: course.startDate,
    end: course.endDate,
    allDay: true,
    color: course.color || '#3788d8',
    extendedProps: {
      type: 'course',
      description: getPlainTextFromDraft(course.description)
    }
  }));

  // Map announcements to calendar events
  const announcementEvents = announcements.map(announcement => ({
    id: `announcement-${announcement._id}`,
    title: `📢 ${announcement.title}`,
    start: announcement.date,
    allDay: true,
    color: '#f59e0b',
    extendedProps: {
      type: 'announcement',
      content: announcement.content,
      createdAt: announcement.createdAt
    }
  }));

  const allEvents = [...courseEvents, ...announcementEvents];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-600 text-lg">Loading calendar data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">Course Schedule</h1>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          events={allEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDidMount={(info) => {
            const description = info.event.extendedProps.type === 'course' 
              ? info.event.extendedProps.description 
              : info.event.extendedProps.content;
            info.el.setAttribute("title", description);
          }}
        />
      </div>

      {/* Create Announcement Modal */}
      {showAnnouncementModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '100%',
            maxWidth: '600px',
            margin: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{fontSize: '20px', fontWeight: 'bold', marginBottom: '16px'}}>
              Create Announcement
            </h2>
            <p style={{color: '#6b7280', marginBottom: '16px'}}>
              Date: {selectedDate}
            </p>
            
            <div style={{marginBottom: '16px'}}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Title
              </label>
              <input
                type="text"
                value={announcementTitle}
                onChange={(e) => setAnnouncementTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Enter announcement title"
              />
            </div>
            
            <div className="mb-6">
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Content
              </label>
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                minHeight: '200px'
              }}>
                <Editor
                  editorState={announcementContentEditorState}
                  wrapperClassName="demo-wrapper"
                  editorClassName="demo-editor"
                  onEditorStateChange={setAnnouncementContentEditorState}
                  toolbar={{
                    options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link', 'history'],
                    inline: {
                      inDropdown: false,
                      options: ['bold', 'italic', 'underline']
                    },
                    blockType: {
                      inDropdown: true,
                      options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']
                    },
                    fontSize: {
                      options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96]
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered']
                    },
                    textAlign: {
                      inDropdown: false,
                      options: ['left', 'center', 'right', 'justify']
                    },
                    link: {
                      inDropdown: false,
                      options: ['link', 'unlink']
                    }
                  }}
                />
              </div>
            </div>
            
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px'}}>
              <button
                onClick={handleCloseModal}
                style={{
                  padding: '8px 16px',
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAnnouncement}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Announcement Modal */}
      {showViewModal && selectedEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            margin: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <p style={{color: '#6b7280', marginBottom: '16px'}}>
              Date: {selectedEvent.start.toDateString()}
            </p>
            <h2 style={{fontSize: '20px', fontWeight: 'bold', marginBottom: '16px'}}>
              {selectedEvent.title.replace('📢 ', '')}
            </h2>
            <div className="mb-6">
              <div 
                style={{
                  color: '#6b7280', 
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5'
                }}
                dangerouslySetInnerHTML={{__html: selectedEvent.extendedProps.content}}
              />
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
              {canCreateAnnouncement && (
                <button
                  onClick={handleDeleteAnnouncement}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  Delete
                </button>
              )}
              <button
                onClick={handleCloseModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}

export default CalendarView;